// react
import { useCallback, useMemo, useState } from 'react';

// components
import { Dialog } from '@/components/atoms';
import { createFieldHelper, Editor } from '@/components/organisms';
import { TablePage } from '../TablePage/TablePage';

// models
import { ActionPlanItem, FieldType } from '@karehero/models';

// tanstack
import { createColumnHelper } from '@tanstack/react-table';

// columns
const columnHelper = createColumnHelper<ActionPlanItem>();

const columns = [
  columnHelper.accessor('id', {
    header: 'ID',
  }),
  columnHelper.accessor('title', {
    header: 'Title',
  }),
  columnHelper.accessor('sanitySlug', {
    header: 'Sanity Slug',
  }),
  columnHelper.accessor('route', {
    header: 'Route',
  }),
];

/**
 * ActionPlanTable is the dashboard page.
 */
export const ActionPlanTable = ({
  isEditable = true,
  actionPlanItems,
  updateActionPlan,
}: ActionPlanTableProps) => {
  // state
  const [selectedActionPlanItem, setSelectedActionPlanItem] = useState<
    Partial<ActionPlanItem>
  >({});
  const [isActionPlanItemEditorOpen, setIsActionPlanItemEditorOpen] =
    useState<boolean>(false);

  // methods
  const handleSave = useCallback(() => {
    updateActionPlan?.({
      ...selectedActionPlanItem,
    } as ActionPlanItem);
    setIsActionPlanItemEditorOpen(false);
  }, [selectedActionPlanItem, updateActionPlan]);

  // memo
  const actions = useMemo(() => {
    return [
      {
        label: 'Edit',
        onPress: (actionPlanItem: ActionPlanItem) => {
          setSelectedActionPlanItem({ ...actionPlanItem });
          setIsActionPlanItemEditorOpen(true);
        },
      },
    ];
  }, []);

  const fields = useMemo(() => {
    return createFieldHelper<ActionPlanItem>({
      sanitySlug: {
        label: 'Sanity Slug',
        field: {
          type: FieldType.Text,
        },
      },
      route: {
        label: 'Route',
        field: {
          type: FieldType.Text,
        },
      },
    });
  }, []);

  return (
    <>
      <TablePage
        title={`Action Plan Items`}
        columns={columns}
        rows={actionPlanItems || []}
        actions={isEditable ? actions : []}
        toolbarActions={[]}
      />
      <Dialog
        title='Edit Action Plan Item'
        isCloseButton
        isOpen={isActionPlanItemEditorOpen}
        setIsOpen={setIsActionPlanItemEditorOpen}
      >
        <Editor
          onSave={handleSave}
          onCancel={() => setIsActionPlanItemEditorOpen(false)}
          saveLabel='Save'
          cancelLabel='Cancel'
          isWithArrows={false}
          isActionsDisabled={false}
          value={selectedActionPlanItem}
          onChange={(res) => {
            setSelectedActionPlanItem((prev) => ({
              ...prev,
              ...res,
            }));
          }}
          fields={fields}
          isFloatingActions={true}
          isForm={true}
        />
      </Dialog>
    </>
  );
};

interface ActionPlanTableProps {
  isEditable?: boolean;
  actionPlanItems?: ActionPlanItem[];
  updateActionPlan?: (actionPlanItem: ActionPlanItem) => void;
}

ActionPlanTable.displayName = 'ActionPlanTable';

export default ActionPlanTable;
